/** Framefield 1.x gallery state is separate from ranked recommendations. */
export interface PortfolioGalleryState {
  gridId: string;
  preset: string;
  filters: { categories: string[]; facets: Record<string, string[]>; query: string };
  visibleProjectIds: string[];
}

/** Known renamed public goal terms; never infer relevance from visibility alone. */
export function portfolioGoalMatches(actual: string, requested: string): boolean {
  const canonical = (value: string): string => ({
    'mitarbeitende-gewinnen': 'mitarbeitergewinnung',
    'mitarbeiter-gewinnen': 'mitarbeitergewinnung',
    'erklaren-verstehen': 'erklaeren-verstehen',
  }[value] ?? value);
  return Boolean(requested) && actual.split(/\s+/).some(value => canonical(value) === canonical(requested));
}

export function acceptsPortfolioMessage(value: unknown, flow: string): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const payload = value as Record<string, unknown>;
  return (payload.flow == null || payload.flow === '' || payload.flow === flow)
    && (payload.bridgeVersion == null || payload.bridgeVersion === ''
      || (typeof payload.bridgeVersion === 'string' && payload.bridgeVersion.split('.')[0] === '1'));
}

/** Only bounded, textual advisor answers may become new matching evidence. */
export function readPortfolioAnswers(payload: Record<string, unknown>): { question: string; answer: string }[] {
  if (!Array.isArray(payload.answers)) return [];
  return payload.answers.slice(0, 50).flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const raw = value as Record<string, unknown>;
    if ((raw.question != null && typeof raw.question !== 'string') || (raw.answer != null && typeof raw.answer !== 'string')) return [];
    const question = typeof raw.question === 'string' ? raw.question.slice(0, 500) : '';
    const answer = typeof raw.answer === 'string' ? raw.answer.slice(0, 500) : '';
    return question || answer ? [{ question, answer }] : [];
  });
}

export function readPortfolioGallery(payload: Record<string, unknown>): PortfolioGalleryState {
  const strings = (value: unknown): string[] => Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string').slice(0, 200) : [];
  const filters = payload.filters && typeof payload.filters === 'object' ? payload.filters as Record<string, unknown> : {};
  const facets = filters.facets && typeof filters.facets === 'object' ? filters.facets : {};
  return {
    gridId: String(payload.gridId ?? ''), preset: String(payload.preset ?? ''),
    filters: { categories: strings(filters.categories), query: typeof filters.query === 'string' ? filters.query : '',
      facets: Object.fromEntries(Object.entries(facets).map(([key, value]) => [key, strings(value)])) },
    visibleProjectIds: strings(payload.visibleProjectIds),
  };
}
